import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getShopifyId, UserState } from 'src/app/shared/state/user/user.reducer';
import { ShakespeareService } from '../shakespeare.service';
import { ShopifyModalComponent } from './shopify-modal/shopify-modal.component';
import { ShopifyService } from './shopify.service';

@Component({
	selector: 'app-shopify',
	templateUrl: './shopify.component.html',
	styleUrls: ['./shopify.component.scss']
})
export class ShopifyComponent implements OnInit, OnDestroy {
	historyData = [];
	otherItems = [];
	menuExpanded: boolean;
	private unsubscriber$ = new Subject<void>();
	shopifyId: string;
	totalNonPerforming: number;
	totalPerforming: number;
	totalProducts = 1; // to overcome divide by zero error in initial state
	optimizedProducts = 0;
	unOptimizedProducts = 0;
	historyLoading: boolean;
	prodLoading: boolean;
	optLoading: boolean;
	allProducts = [];
	newlyLoading: boolean;
	perfLoading: boolean;
	Math = Math;
	flagLoading: boolean;
	scrWidth: number;
	newItems = [];
	popupFlag: boolean;

	constructor(
		private router: Router,
		private shakespeareService: ShakespeareService,
		private shopifyService: ShopifyService,
		private userStore: Store<UserState>,
		private dialog: MatDialog
	) {
		this.shakespeareService.shopifyActive$.next(true);
		this.userStore.pipe(select(getShopifyId), takeUntil(this.unsubscriber$)).subscribe(id => {
			this.shopifyId = id;
		});
	}

	@HostListener('window:resize', ['$event'])
	getScreenSize(event?): void {
		this.scrWidth = window.innerWidth;
	}

	ngOnInit(): void {
		this.scrWidth = window.innerWidth;
		this.getOptimizedProducts();
		this.getPerformingProducts();
		this.getProductsHistory();
		this.getAllProducts();
		this.getFlags();
	}

	getOptimizedProducts(): void {
		this.optLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {}
		};
		this.shopifyService
			.getOptimizedProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.optLoading = false;
					this.optimizedProducts = response['data']['optimizedProducts'];
					this.unOptimizedProducts = response['data']['unoptimizedProducts'];
				},
				() => {
					this.optLoading = false;
				},
				() => {
					this.optLoading = false;
				}
			);
	}

	getAllProducts(page = 1): void {
		this.prodLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {
				limit: 4,
				page: page
			}
		};
		this.shopifyService
			.getStoreProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					if (response['data'][0]['debugMessage'] == 'Page not found') {
						return;
					}
					this.allProducts = response['data'];
					this.allProducts.forEach(item => {
						item.optimizationScore = Math.round(item.optimizationScore);
					});
					this.prodLoading = false;
				},
				() => {
					this.prodLoading = false;
				},
				() => {
					this.prodLoading = false;
				}
			);
	}

	getNewlyGenerated(page = 1): void {
		this.newlyLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {
				limit: 4,
				page: page
			}
		};
		this.shopifyService
			.getNewlyGenerated(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.newItems = response['data'].generatedData;
					this.newlyLoading = false;
				},
				() => {
					this.newlyLoading = false;
				},
				() => {
					this.newlyLoading = false;
				}
			);
	}

	getPerformingProducts(): void {
		this.perfLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {}
		};
		this.shopifyService
			.getPerformingProducts(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					console.log(response);
					this.totalNonPerforming = response['data']['totalNonPerforming'];
					this.totalPerforming = response['data']['totalPerforming'];
					this.totalProducts = this.totalPerforming + this.totalNonPerforming || 1;
					this.perfLoading = false;
				},
				() => {
					this.perfLoading = false;
				},
				() => {
					this.perfLoading = false;
				}
			);
	}

	getProductsHistory(): void {
		this.historyLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {
				page_number: 1
			}
		};
		this.shopifyService
			.getProductsHistory(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.historyLoading = false;
					console.log(response);
					this.historyData = response['data'].history;
				},
				() => {
					this.historyLoading = false;
				},
				() => {
					this.historyLoading = false;
				}
			);
	}

	getFlags(): void {
		this.flagLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {}
		};
		this.shopifyService
			.bulkFlagRetrive(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.flagLoading = response['flags'].generatedFlag;
					this.popupFlag = response['flags'].popUpFlag;
					this.shopifyService.canGenerate$.next(!this.flagLoading);
					if (this.popupFlag) {
						this.openSuccessModal();
					}
					if (!this.flagLoading) {
						this.getNewlyGenerated();
					} else {
						setTimeout(() => {
							this.getFlags();
						}, 20000);
					}
				},
				() => {
					this.flagLoading = false;
				}
			);
	}

	openSuccessModal(): void {
		let modalConfig;
		let isMobile = this.scrWidth < 780 ? true : false;
		if (isMobile) {
			modalConfig = {
				width: '90%',
				height: '316px',
				maxWidth: '100%',
				hasBackdrop: false
			};
		} else {
			modalConfig = {
				width: '436px',
				height: '316px',
				hasBackdrop: true
			};
		}
		const dialogRef = this.dialog.open(ShopifyModalComponent, {
			...modalConfig,
			panelClass: 'modal-wrapper',
			disableClose: true,
			data: {
				title: 'Successful!',
				desc: 'Your new descriptions are generated successfully!',
				isCancel: false,
				img: 'assets/icons/big-check.svg',
				cta: 'View Descriptions'
			}
		});
		dialogRef.afterClosed().subscribe(res => {
			if (res.confirm) {
				this.viewAllNewProducts();
			}
			this.resetFlag();
		});
	}

	resetFlag(): void {
		this.flagLoading = true;
		const payload = {
			user: {
				user_filed_id: this.shopifyId
			},
			data: {}
		};
		this.shopifyService
			.bulkFlagReset(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				response => {
					this.flagLoading = response['flags'].generatedFlag;
				},
				() => {
					this.flagLoading = false;
				}
			);
	}

	viewAllProducts(): void {
		this.router.navigate(['shopify/all-products']);
	}

	viewAllNewProducts(): void {
		this.router.navigate(['shopify/all-products'], { queryParams: { type: 'newly-generated' } });
	}

	viewProducts(type: string): void {
		this.router.navigate(['shopify/all-products'], { queryParams: { type: type } });
	}

	showHistory(): void {
		this.router.navigate(['shopify/all-products'], { queryParams: { type: 'history' } });
	}

	openProduct(id: number): void {
		this.router.navigate(['shopify/generate'], { queryParams: { productId: id } });
	}

	ngOnDestroy(): void {
		this.unsubscriber$.unsubscribe();
	}
}
